import { Grid } from "components/grid";
import Icon from "components/icon";
import TinyBadge from "components/tiny-badge";
import { FindexClient } from "@/integrations/findex";
import { uref } from "@/utils/reactive/uref";
import { type IAttachment, type TDocRef } from "findef";
import { computed, defineComponent, type MaybeRef } from "vue";
import RocketIcon from "assets/img/svg/icons/rocket.svg?url";
import { twColor } from "@/utils/style";
import type { IconFactor } from "@/types/elements/icon";
import { useAsyncFetch } from "@/utils/async/useAsyncFetch";
import { checkValidImage } from "@/utils/image";
import { Spinner } from "components/spinner";
import { useResponsive } from "~/composables/useResponsive";

export const UnlistedCompanyOption = defineComponent<{
  image?: TDocRef<IAttachment>;
  imageSrc?: string;
  orgNbr?: string;
  name: string;
  badge?: string;
  badge2?: string;
  isOpen?: MaybeRef<boolean>;
  listed?: boolean;
}>(
  (props) => {
    const { isSmall } = useResponsive();
    const isMobile = computed(() => isSmall.value);
    const isOpen = computed(() => !!uref(props.isOpen));
    const name = computed(() => props.name);
    const orgNbr = computed(() =>
      props.orgNbr && props.orgNbr.trim().length > 0 && props.orgNbr !== ""
        ? props.orgNbr
        : "No identifier",
    );
    const badge = computed(() => props.badge);
    const badge2 = computed(() => props.badge2);

    const iconSource = computed((): IconFactor => {
      if (props.listed) {
        const iconSource: IconFactor = {
          src: async () => import("assets/img/svg/icons/file-copy2.svg?raw"),
          size: 18,
        };
        return iconSource;
      }
      return RocketIcon;
    });

    const imageSource = computed(() => props.image ? FindexClient.urlForAttachment(props.image) : props.imageSrc);

    const isValidImage = useAsyncFetch(async () => {
      const src = imageSource.value;
      if (!src) return false;
      return await checkValidImage(src);
     }, false);

    return () => {
      return (
        <Grid
          class="w-full h-full min-w-0"
          columns="minmax(0, 1fr) max-content"
          alignItems="center"
          gap="0.75rem"
        >
          <Grid columns="max-content 1fr" alignItems="center" gap="0.625rem" class="min-w-0">
            <div
              class={[
                "justify-center items-center flex",
                isOpen.value ? "w-10 h-10" : "w-6 h-6",
                "bg-gray-100",
              ]}
              style={{ aspectRatio: "1 / 1" }}
            >
              {isValidImage.loading.value ? (
                <Spinner />
              ) : imageSource.value && isValidImage.response.value ? (
                <img src={imageSource.value}/>
              ) : (
                <Icon
                  size={18}
                  src={iconSource.value}
                  {...props}
                  fill={twColor("gray-400")}
                  stroke="none"
                  {...props}
                />
              )}
              {/* {imageLoading.value && (
                <div class="absolute w-full h-full left-0 top-0 bg-gray-100 justify-center items-center flex">
                  <Spinner />
                </div>
              )} */}
            </div>
            {isOpen.value ? (
              <div class="flex-col justify-start items-start flex min-w-0 max-w-full">
                <div class="text-[#111928] text-xs font-semibold font-['Inter'] truncate w-full">
                  {name.value}
                </div>
                <div class="text-gray-500 text-xs font-normal font-['Inter'] wrap-break-word">
                  {orgNbr.value}
                </div>
              </div>
            ) : (
              <div class="text-[#111928] text-sm font-normal font-['Inter'] flex items-center min-w-0">
                <span class="truncate">{name.value}</span>
              </div>
            )}
          </Grid>
          {isOpen.value ? (
            !isMobile.value && (badge.value || badge2.value) && (
              <Grid
                noDefaultLayout
                columns="max-content max-content"
                gap={badge2.value ? "1rem" : "0"}
                alignItems="center"
              >
                {badge.value && (
                    <TinyBadge
                      style={{
                        height: "22px",
                        paddingTop: "0px",
                        paddingBottom: "0px",
                       }}
                      variant="gray"
                      type="mltslct"
                      textClass="text-xs font-semibold"
                    >
                      {badge.value}
                    </TinyBadge>
                )}
                {badge2.value && (
                  <div>
                    <TinyBadge
                      style={{
                        height: "22px",
                        paddingTop: "0px",
                        paddingBottom: "0px",
                      }}
                      variant="gray"
                      type="mltslct"
                      textClass="text-xs font-semibold"
                    >
                      {badge2.value}
                    </TinyBadge>
                  </div>
                )}
              </Grid>
            )
          ) : (
            <div>
              <span class="text-gray-500 text-sm font-normal font-['Inter']">
                {orgNbr.value}
              </span>
            </div>
          )}
        </Grid>
      );
    };
  },
  {
    props: [
      "orgNbr",
      "badge",
      "badge2",
      "name",
      "image",
      "isOpen",
      "listed",
      "imageSrc",
    ],
  },
);
